import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { articles, getArticlesByCategory } from '../data/articles';
import { ArticleMeta } from '../types/article';
import AdSense from '../components/ads/AdSense';

const ArticleList: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'すべて' },
    { id: 'ranking', name: 'ランキング' },
    { id: 'blog', name: 'ブログ' },
    { id: 'analysis', name: '分析' }
  ];

  const allTags = Array.from(new Set(articles.flatMap(article => article.tags)));

  const filteredArticles = articles.filter(article => {
    const categoryMatch = selectedCategory === 'all' || article.category === selectedCategory;
    const tagMatch = selectedTag === 'all' || article.tags.includes(selectedTag);
    return categoryMatch && tagMatch;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  // BreadcrumbList構造化データ生成
  const getBreadcrumbData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "https://niche-football.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "記事・ランキング",
          "item": "https://niche-football.com/articles"
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>サッカー記事・ランキング | 【ニッチな分析】ダッシュボード</title>
        <meta name="description" content="高校サッカーの最新情報、ランキング、分析記事をお届けします。データに基づいた詳細な分析とランキング情報を提供しています。" />
        <meta property="og:title" content="サッカー記事・ランキング | 【ニッチな分析】ダッシュボード" />
        <meta property="og:description" content="高校サッカーの最新情報、ランキング、分析記事をお届けします。データに基づいた詳細な分析とランキング情報を提供しています。" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://niche-football.com/articles" />
        <meta property="twitter:title" content="サッカー記事・ランキング | 【ニッチな分析】ダッシュボード" />
        <meta property="twitter:description" content="高校サッカーの最新情報、ランキング、分析記事をお届けします。データに基づいた詳細な分析とランキング情報を提供しています。" />
        <meta property="twitter:card" content="summary_large_image" />
        
        {/* BreadcrumbList構造化データ */}
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbData())}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            サッカー記事・ランキング
          </h1>
          <p className="text-xl text-gray-600">
            高校サッカーの最新情報、ランキング、分析記事をお届けします
          </p>
        </div>

        {/* フィルター */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリー
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                タグ
              </label>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">すべて</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 記事一覧 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article, index) => (
            <React.Fragment key={article.id}>
              <article className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {article.thumbnail && (
                  <div className="aspect-video bg-gray-200">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      article.category === 'ranking' ? 'bg-red-100 text-red-800' :
                      article.category === 'blog' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {article.category === 'ranking' ? 'ランキング' :
                       article.category === 'blog' ? 'ブログ' : '分析'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(article.publishedAt)}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                    {article.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.description}
                  </p>

                  {article.youtubeUrl && (
                    <div className="mb-4">
                      <a
                        href={article.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        YouTube動画を見る
                      </a>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={`/article/${article.id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    続きを読む
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </article>
              
              {/* 3記事ごとに広告を挿入 */}
              {(index + 1) % 3 === 0 && (
                <div className="col-span-full my-6">
                  <AdSense
                    client="ca-pub-YOUR_PUBLISHER_ID"
                    slot="YOUR_AD_SLOT_ID"
                    format="auto"
                    responsive={true}
                    className="text-center"
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              条件に一致する記事が見つかりませんでした。
            </p>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ArticleList;
